<template>
  <view class="pft-data-text-container" :style="style" :flex="flex">
    <view class="text-title" :style="titleStyle" @click="emits('title-click')" :flex="titleFlex">
      <view>{{ titleVal }}</view>
      <view flex="dir:left main:center cross:center">
        <slot name="extra"></slot>
      </view>
    </view>
    <template v-if="componentLoading">
      <Loading />
    </template>
    <view v-else class="text-data" :flex="dataContainerFlex" :style="dataContainerStyle">
      <view class="data-val" :style="dataStyle">
        <template v-if="!flop">
          {{ formatData(dataVal || data) }}
        </template>
        <canvas v-else ref="digitalFlop" :height="canvasHeight" :width="canvasWidth" />
      </view>
      <view class="data-suffix" v-if="suffix" :style="suffixStyle">
        {{ typeof suffix === "function" ? suffix(dataVal || data) : suffix }}
      </view>
      <view class="data-percent" :style="percentStyle" v-if="percent">{{ percent }}</view>
      <template v-if="showDirection">
        <img
          :src="isIncrease ? upIcon : downIcon"
          :style="{
            width: directionSize,
            height: directionSize,
          }"
        />
      </template>
      <!-- <template v-if="/\d+/.test(data) === false">{{ data }}</template>
        <template v-else>
          <DigitalFlop :config="{ number: [Number(data)], content: '{nt}' }" />
        </template> -->
    </view>
  </view>
</template>
<script lang="ts" setup>
import { ref, nextTick, computed } from "vue"
import Loading from "@p-v/packages/Loading.vue"
import numeral from "@p-v/utils/numeral"
import CRender from "@jiaminghi/c-render"
import type { Graph } from "@jiaminghi/c-render"
import "@jiaminghi/charts/lib/extend/index"
import { defaultsDeep } from "lodash-es"

import upIcon from "@p-v/asset/image/icon_up@2x.png"
import downIcon from "@p-v/asset/image/icon_down@2x.png"

defineOptions({
  name: "PftDataText",
})

const emits = defineEmits(["title-click"])

interface DigitalFlop {
  number?: number[]
  content?: string
  toFixed: number
  textAlign: "center" | "left" | "right"
  rowGap: number
  style: { [key: string]: number | string | boolean }
  formatter: ((data: string | number) => string) | undefined
  animationCurve: string
  animationFrame: number
}

interface DataTextProps {
  title: string
  data?: string | number
  format?: string | ((data: string | number) => string)
  suffix?: string | ((data: string | number) => string)
  flex?: string
  titleFlex?: string
  dataContainerFlex?: string
  style?: { [key: string]: string | number }
  titleStyle?: { [key: string]: string | number }
  dataStyle?: { [key: string]: string | number }
  suffixStyle?: { [key: string]: string | number }
  dataContainerStyle?: { [key: string]: string | number }
  percentStyle?: { [key: string]: string | number }
  empty?: string
  flop?: boolean
  flopConfig?: DigitalFlop
  showDirection?: boolean
  directionSize?: string
}

const digitalFlop = ref<HTMLCanvasElement | null>(null)
const canvasHeight = ref<number>(10)
const canvasWidth = ref<number>(10)

let digitalFlopInstance: CRender | undefined
let mergedConfig: DigitalFlop | undefined
let graph: Graph

const props = withDefaults(defineProps<DataTextProps>(), {
  title: () => "",
  data: () => "",
  suffix: undefined,
  flex: () => "dir:top main:justify cross:center",
  titleFlex: () => "dir:left main:center cross:center",
  dataContainerFlex: () => "dir:left cross:bottom",
  format: () => "0,0",
  style: () => ({}),
  titleStyle: () => ({}),
  dataStyle: () => ({
    fontSize: "28px",
    lineHeight: "33px",
    fontWeight: "bold",
  }),
  dataContainerStyle: () => ({}),
  percentStyle: () => ({
    fontSize: "12px",
    lineHeight: "15px",
    color: "#fcf072",
  }),
  empty: undefined,
  flop: () => false,
  flopConfig: () => ({
    /**
     * @description Number toFixed
     * @type {Number}
     * @default toFixed = 0
     */
    toFixed: 0,
    /**
     * @description Text align
     * @type {String}
     * @default textAlign = 'center'
     * @example textAlign = 'center' | 'left' | 'right'
     */
    textAlign: "center",
    /**
         * @description rowGap
         * @type {Number}
         @default rowGap = 0
         */
    rowGap: 0,
    /**
     * @description Text style configuration
     * @type {Object} {CRender Class Style}
     */
    style: {},
    /**
     * @description Number formatter
     * @type {Null|Function}
     */
    formatter: undefined,
    /**
     * @description CRender animationCurve
     * @type {String}
     * @default animationCurve = 'easeOutCubic'
     */
    animationCurve: "easeOutCubic",
    /**
     * @description CRender animationFrame
     * @type {String}
     * @default animationFrame = 50
     */
    animationFrame: 50,
  }),
  showDirection: false,
  directionSize: () => "12px",
})

const titleVal = ref<string>(props.title)

const formatData = (val: string | number) => {
  if (val === undefined || val === null || val == 0) {
    if (props.empty) return props.empty
    if (val == 0) {
      if (typeof props.format === "function") {
        return props.format(val)
      }
      return numeral(Number(val)).format(props.format)
    } else {
      return ""
    }
  }
  if (typeof val === "string" && /\d+/.test(val) === false) {
    return val
  }
  if (typeof props.format === "function") {
    return props.format(val)
  }
  return numeral(Number(val)).format(props.format)
}

const componentLoading = ref<boolean>(true)

const dataVal = ref<string | number>("")
const percent = ref<number>(0)

const isIncrease = computed(() => {
  if (typeof dataVal.value === "string") return Number(dataVal.value) > 0
  return dataVal.value > 0
})

const loading = () => {
  componentLoading.value = true
}

const isFirstRender = ref(true)

const setData = (val: string | { val: string; rate?: number; title?: string }, rate?: number) => {
  componentLoading.value = false
  if (typeof val === "object") {
    dataVal.value = val.val
    percent.value = val.rate || 0

    if (val.title) {
      titleVal.value = val.title
    }
  } else {
    dataVal.value = val
    percent.value = rate || 0
  }
  if (props.flop) {
    if (isFirstRender.value) {
      nextTick(() => {
        if (initRender()) {
          mergeConfigFn(0)
          initGraph()
          updateGraph()
          isFirstRender.value = false
        }
      })
    } else {
      updateGraph()
    }
  }
}

const initRender = () => {
  if (digitalFlopInstance) return
  if (digitalFlop.value) {
    canvasHeight.value = props.dataStyle.lineHeight
      ? parseInt(props.dataStyle.lineHeight as string)
      : props.dataStyle.fontSize
        ? parseInt(props.dataStyle.fontSize as string) * 1.2
        : 33
    // console.log(canvasHeight.value, 28 * 1.2)
    digitalFlopInstance = new CRender(digitalFlop.value)
    // digitalFlopInstance.config(defaultConfig)
    // digitalFlopInstance.init()
    return true
  }
  return false
}

const mergeConfigFn = (data: number | string) => {
  if (digitalFlopInstance) {
    digitalFlopInstance.ctx.font = `${props.dataStyle.fontSize} Arial`
    const metrics = digitalFlopInstance.ctx.measureText(formatData(data))
    canvasWidth.value = metrics.width
    digitalFlopInstance.area = [canvasWidth.value, canvasHeight.value]
  }
  mergedConfig = defaultsDeep(
    {
      number: [data],
      content: "{nt}",
      formatter: formatData,
    },
    {
      style: {
        fontSize: parseInt(props.dataStyle.fontSize as string),
        fontWeight: props.dataStyle.fontWeight || "normal",
        fill: props.dataStyle.color || "#2bb7f7",
      },
      textAlign: props.dataStyle.textAlign || "center",
    },
    props.flopConfig,
  )
}

const getShape = () => {
  if (!mergedConfig) return
  if (!digitalFlopInstance) return
  const { number, content, toFixed, textAlign, rowGap, formatter } = mergedConfig

  const [w, h] = digitalFlopInstance.area

  const position = [w / 2, h / 2]

  if (textAlign === "left") position[0] = 0
  if (textAlign === "right") position[0] = canvasWidth.value

  return {
    number,
    content,
    toFixed,
    position,
    rowGap,
    formatter,
  }
}

const getStyle = () => {
  if (!mergedConfig) return
  const { style, textAlign } = mergedConfig

  return defaultsDeep(style, {
    textAlign,
    textBaseline: "middle",
  })
}

const initGraph = () => {
  if (!mergedConfig) return
  if (!digitalFlopInstance) return
  const { animationCurve, animationFrame } = mergedConfig
  const shape = getShape()
  const style = getStyle()
  graph = digitalFlopInstance.add({
    name: "numberText",
    animationCurve,
    animationFrame,
    shape,
    style,
  })
}

const mergeShape = (graph, shape) => {
  const cacheNum = graph.shape.number.length
  const shapeNum = shape.number.length

  if (cacheNum !== shapeNum) graph.shape.number = shape.number
}

const updateGraph = () => {
  graph.animationEnd()

  mergeConfigFn(dataVal.value)

  if (!graph || !mergedConfig) return

  const { animationCurve, animationFrame } = mergedConfig

  const shape = getShape()
  const style = getStyle()

  mergeShape(graph, shape)

  graph.animationCurve = animationCurve
  graph.animationFrame = animationFrame

  graph.animation("style", style, true)
  graph.animation("shape", shape)
}

defineExpose({ loading, setData })
</script>
<style lang="scss">
.pft-data-text-container {
  box-sizing: border-box;
  width: 100%;
  .text-title {
    height: 20px;
    font-family:
      "Segoe UI",
      Segoe UI;
    font-weight: 400;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.85);
    line-height: 20px;
    margin-bottom: 8px;
    flex-shrink: 0;
  }
  .text-data {
    // width: 100%;
    .data-val {
      font-family:
        "Segoe UI",
        Segoe UI;
      font-weight: bold;
      font-size: 28px;
      color: #2bb7f7;
      line-height: 33px;
    }
    .data-suffix {
      font-family:
        Segoe UI,
        Segoe UI;
      font-weight: 400;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.65);
      margin-left: 2px;
    }
    .data-percent {
      font-family:
        Segoe UI,
        Segoe UI;
      font-weight: 400;
      font-size: 12px;
      color: #fcf072;
      margin-left: 8px;
    }
  }
}
</style>
